import Breadcrumb from "components/Common/Breadcrumb"
import { useMemo, useState } from "react"
import { FaEdit, FaPlus, FaSave } from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Button,
  Card,
  CardBody,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap"
import React from "react"
import { usePermissionData, usePrioritiesData } from "AppContext"
import Swal from "sweetalert2"
import moment from "moment"

const EMPTY_FORM = { priorityName: "", unit: "hours", value: "" }

const Priorities = () => {
  document.title = "Priorities | Vintage Infra Equipment Sol PVT LTD"
  const { priorities: data, setPriorities: setData } = usePrioritiesData()
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedPriority, setSelectedPriority] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  const { checkAccess } = usePermissionData()

  const filteredData = useMemo(() => {
    return data.filter(item =>
      item?.priorityName?.toLowerCase().includes(searchString.toLowerCase())
    )
  }, [data, searchString])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const openAddModal = () => {
    setSelectedPriority(EMPTY_FORM)
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setSelectedPriority({ ...item })
    setErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedPriority?.priorityName?.trim()) {
      newErrors.priorityName = "Priority name is required"
    }
    if (!selectedPriority?.unit) {
      newErrors.unit = "Please select a unit"
    }
    const value = Number(selectedPriority?.value)
    if (
      selectedPriority?.value === "" ||
      selectedPriority?.value === null ||
      isNaN(value) ||
      value <= 0 ||
      !Number.isInteger(value)
    ) {
      newErrors.value = "Value must be a positive whole number"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Design-first: local CRUD only (no API yet)
  const handleSubmit = () => {
    if (!validate()) return

    const trimmed = {
      priorityName: selectedPriority.priorityName.trim(),
      unit: selectedPriority.unit,
      value: Number(selectedPriority.value),
    }

    if (selectedPriority?.id) {
      setData(prev =>
        prev.map(item =>
          item.id === selectedPriority.id ? { ...item, ...trimmed } : item
        )
      )
      Swal.fire({
        title: "Priority Updated Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      const newItem = {
        id: Date.now(),
        ...trimmed,
        createdAt: new Date(),
      }
      setData(prev => [newItem, ...prev])
      Swal.fire({
        title: "Priority Added Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    }
    setModalOpen(false)
    setSelectedPriority(EMPTY_FORM)
    setCurrentPage(1)
    // TODO: replace with FetchPostData / FetchPutData "priorities" when API is ready
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.priorityName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setData(prev => prev.filter(d => d.id !== item.id))
        Swal.fire({
          title: "Priority Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "priorities" when API is ready
      }
    })
  }

  return (
    <div className="page-content">
      <Breadcrumb title="Dashboard" breadcrumbItem="Priorities" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value)), setCurrentPage(1)
                }}
                className="rounded-4 w-auto pe-5"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4"
                placeholder="Search Priority"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            {checkAccess(10, "view") && (
              <Button className="btn-blue rounded-4 px-3" onClick={openAddModal}>
                <FaPlus className="me-2" />
                Priority
              </Button>
            )}
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th style={{ width: "60px" }}>#</th>
                <th>Priority Name</th>
                <th style={{ width: "120px" }}>Unit</th>
                <th style={{ width: "120px" }}>Value</th>
                <th>Created At</th>
                <th className="text-center" style={{ width: "140px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Priorities found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{start + index + 1}</td>

                    <td>{item.priorityName}</td>
                    <td>{item.unit === "hours" ? "Hours" : "Days"}</td>
                    <td>{item.value}</td>
                    <td>
                      {moment(item.createdAt).format("DD-MM-YYYY | hh:mm A")}
                    </td>

                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      <Button
                        className="bg-success text-white me-2 border-0 shadow-none"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit size={16} />
                      </Button>

                      <Button
                        className="bg-danger text-white border-0 shadow-none"
                        onClick={() => handleDelete(item)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedPriority?.id ? "Edit" : "Add"} Priority
        </ModalHeader>
        <ModalBody>
          <div>
            <Label>
              Priority Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="e.g. Urgent"
              value={selectedPriority?.priorityName || ""}
              invalid={!!errors.priorityName}
              onChange={e =>
                setSelectedPriority(prev => ({
                  ...prev,
                  priorityName: e.target.value,
                }))
              }
            />
            {errors.priorityName && (
              <FormFeedback className="d-block">
                {errors.priorityName}
              </FormFeedback>
            )}
          </div>
          <div className="mt-3">
            <Label>
              Unit <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={selectedPriority?.unit || "hours"}
              invalid={!!errors.unit}
              onChange={e =>
                setSelectedPriority(prev => ({
                  ...prev,
                  unit: e.target.value,
                }))
              }
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </Input>
            {errors.unit && (
              <FormFeedback className="d-block">{errors.unit}</FormFeedback>
            )}
          </div>
          <div className="mt-3">
            <Label>
              Value <span className="text-danger">*</span>
            </Label>
            <Input
              type="number"
              min="1"
              step="1"
              placeholder="e.g. 4"
              value={selectedPriority?.value ?? ""}
              invalid={!!errors.value}
              onChange={e =>
                setSelectedPriority(prev => ({
                  ...prev,
                  value: e.target.value,
                }))
              }
            />
            {errors.value && (
              <FormFeedback className="d-block">{errors.value}</FormFeedback>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            <FaSave /> Save
          </Button>
          <Button className="btn-danger" onClick={() => setModalOpen(false)}>
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default Priorities
